import _str from "shared/util/_str";
import { makeMetricHeightRange, makeImperialHeightRange } from "./helpers";
import { AVAILABLE_TAGS } from "apps/nonbinary/util/constants";

export const FIELD_TRANSITION_SPEED = 400;
export const FIELDS_MAP = {
	NAME: "name",

	// used for indian user level and education entering
	EDUCATION_LEVEL_AND_SCHOOL: "education",
	OCCUPATION: "occupation", // includes title and employer

	// used for global preference details reboarding
	EDUCATION_LEVEL: "education_level",
	EDUCATION_SCHOOL: "education_school",
	EMPLOYMENT: "occupation_status", // occupation status radios
	OCCUPATION_TITLE: "occupation_title", // occupation with only title, no employer

	RELIGION: ["religion.modifier", "religion.value"],
	RELIGION_MODIFIER: "religion.modifier",
	RELIGION_VALUE: "religion.value",

	CHILDREN: ["children.have", "children.want"],
	CHILDREN_HAVE: "children.have",
	CHILDREN_WANT: "children.want",

	PRONOUNS: "pronouns",

	DIET: "diet",
	SMOKING: "smoking",
	DRINKING: "drinking",
	DRUGS: "drugs",
	HEIGHT: "height",
	ETHNICITIES: "ethnicities",
	POLITICS: "politics",
	GENDER: ["gender.genderTags", "gender.underlyingGender"],
	GENDER_TAGS: "gender.genderTags",
	UNDERLYING_GENDER: "gender.underlyingGender",
	BIRTHDATE: "birthdate",
	LOCATION: "location",
	LANGUAGES: "languages.language",
	SIGNS: "sign",
	PETS: "pets2",
	MARIJUANA: "weed",
	BODY_TYPE: "bodytype",
	ORIENTATION_TAGS: "orientation_tags",
};

export const FIELD_PAIRS = {
	religion: [FIELDS_MAP.RELIGION_MODIFIER, FIELDS_MAP.RELIGION_VALUE],
	children: [FIELDS_MAP.CHILDREN_HAVE, FIELDS_MAP.CHILDREN_WANT],
	gender: [FIELDS_MAP.GENDER_TAGS, FIELDS_MAP.UNDERLYING_GENDER],
};

export const FIELDS_ORDER_ONBOARDING = [
	FIELDS_MAP.NAME,
	FIELDS_MAP.GENDER_TAGS,
	FIELDS_MAP.BIRTHDATE,
	FIELDS_MAP.LOCATION,
];

export const FIELDS_ORDER_PROFILE_DETAILS = [
	FIELDS_MAP.ORIENTATION_TAGS,
	FIELDS_MAP.BODY_TYPE,
	FIELDS_MAP.HEIGHT,
	FIELDS_MAP.LANGUAGES,
	FIELDS_MAP.ETHNICITIES,
	FIELDS_MAP.RELIGION_VALUE,
	FIELDS_MAP.POLITICS,
	FIELDS_MAP.EDUCATION_LEVEL,
	FIELDS_MAP.EMPLOYMENT,
	FIELDS_MAP.SIGNS,
	FIELDS_MAP.DRINKING,
	FIELDS_MAP.SMOKING,
	FIELDS_MAP.MARIJUANA,
	FIELDS_MAP.DIET,
	FIELDS_MAP.PETS,
	FIELDS_MAP.CHILDREN_HAVE,
	FIELDS_MAP.CHILDREN_WANT,
];

export const FIELDS_ORDER_EDUCATION_OCCUPATION = [
	FIELDS_MAP.EDUCATION_LEVEL_AND_SCHOOL,
	FIELDS_MAP.OCCUPATION,
];

export const MULTISELECT_FIELDS = [
	FIELDS_MAP.GENDER_TAGS,
	FIELDS_MAP.ORIENTATION_TAGS,
	FIELDS_MAP.PETS,
	FIELDS_MAP.ETHNICITIES,
	FIELDS_MAP.LANGUAGES,
];

export const DROPDOWN_FIELDS = [
	FIELDS_MAP.PRONOUNS,
];

export const DROPDOWN_PLACEHOLDERS = {
	[FIELDS_MAP.PRONOUNS]: _str("Pronouns"),
};

export const MANDATORY_FIELDS = {
	[FIELDS_MAP.LOCATION]: true,
	[FIELDS_MAP.BIRTHDATE]: true,
	[FIELDS_MAP.GENDER_TAGS]: true,
	[FIELDS_MAP.UNDERLYING_GENDER]: true,
	[FIELDS_MAP.NAME]: true,
};

export const DEFAULT_DATA = {
	[FIELDS_MAP.BIRTHDATE]: {},
	[FIELDS_MAP.EDUCATION_LEVEL_AND_SCHOOL]: { modifier: "&mdash", schools: [{ name: "" }], value: "&mdash;" },
	[FIELDS_MAP.EDUCATION_LEVEL]: "&mdash;",
	[FIELDS_MAP.OCCUPATION]: {},
};

// add in default data for muiltiselect fields
MULTISELECT_FIELDS.forEach((field) => {
	DEFAULT_DATA[field] = [];
});

export const FIELDS_PROMPTS_ONBOARDING = {
	[FIELDS_MAP.NAME]: _str("What’s your first name?"),
	[FIELDS_MAP.GENDER_TAGS]: _str("I am a…"),
	[FIELDS_MAP.UNDERLYING_GENDER]: _str("Include me in searches for"),
	[FIELDS_MAP.BIRTHDATE]: _str("When were you born?"),
	[FIELDS_MAP.LOCATION]: _str("Where do you primarily live?"),
};

export const FIELD_PROMPTS_DETAILS_REBOARDING = {
	[FIELDS_MAP.BODY_TYPE]: _str("Body Type"),
	[FIELDS_MAP.HEIGHT]: _str("Height"),
	[FIELDS_MAP.ORIENTATION_TAGS]: _str("I am a…"),
	[FIELDS_MAP.EDUCATION_LEVEL]: _str("Education Level"),
	[FIELDS_MAP.EDUCATION_SCHOOL]: _str("School Attending"),
	[FIELDS_MAP.EMPLOYMENT]: _str("Employment"),
	[FIELDS_MAP.OCCUPATION]: _str("Occupation"),
	[FIELDS_MAP.OCCUPATION_TITLE]: _str("Occupation"),
	[FIELDS_MAP.LANGUAGES]: _str("Language"),
	[FIELDS_MAP.ETHNICITIES]: _str("Ethnicity"),
	[FIELDS_MAP.RELIGION_VALUE]: _str("Religion"),
	[FIELDS_MAP.POLITICS]: _str("Political Views"),
	[FIELDS_MAP.SIGNS]: _str("Astrological Sign"),
	[FIELDS_MAP.DRINKING]: _str("Alcohol"),
	[FIELDS_MAP.SMOKING]: _str("Smoking"),
	[FIELDS_MAP.MARIJUANA]: _str("Marijuana"),
	[FIELDS_MAP.DIET]: _str("Diet"),
	[FIELDS_MAP.PETS]: _str("Pets"),
	[FIELDS_MAP.CHILDREN_HAVE]: _str("Has kids"),
	[FIELDS_MAP.CHILDREN_WANT]: _str("Want kids"),
};

export const FIELDS_PROMPTS_DEFAULT = {
	[FIELDS_MAP.EDUCATION_LEVEL_AND_SCHOOL]: _str("Where did you study?"),
	[FIELDS_MAP.OCCUPATION]: _str("What do you do for work?"),
	[FIELDS_MAP.RELIGION_VALUE]: _str("Which religion do you identify with?"),
	[FIELDS_MAP.RELIGION_MODIFIER]: "How serious are you about $religionValue?",
	[FIELDS_MAP.CHILDREN_HAVE]: _str("Do you have any kids?"),
	[FIELDS_MAP.CHILDREN_WANT]: _str("Do you want kids?"),
	[FIELDS_MAP.DIET]: _str("Which of these best describe your diet?"),
	[FIELDS_MAP.SMOKING]: _str("Do you smoke tobacco?"),
	[FIELDS_MAP.DRINKING]: _str("How often do you drink alcohol?"),
	[FIELDS_MAP.DRUGS]: _str("How often do you use any drugs?"),
	[FIELDS_MAP.HEIGHT]: _str("How tall are you?"),
	[FIELDS_MAP.ETHNICITIES]: _str("Which ethnicities best describe you?"),
};

export const FIELDS_DETAILS_ONBOARDING = {
	[FIELDS_MAP.UNDERLYING_GENDER]: _str("We know gender is not binary, and it doesn’t feel great to have to choose between these two options. We want you to feel like yourself here, so we’re working on changing this."),
};

export const MAX_COUNT = {
	[FIELDS_MAP.GENDER_TAGS]: 5,
};

export const MODAL_PROMPTS = {
	[FIELDS_MAP.GENDER_TAGS]: _str(`Select up to ${MAX_COUNT[FIELDS_MAP.GENDER_TAGS]}`),
};

export const FIELD_NAVIGATION = {
	NEXT: "next",
	PREVIOUS: "previous",
};

export const UNIT_FIELDS = [
	FIELDS_MAP.HEIGHT,
];

export const HEIGHT_BOUNDS = {
	// in cm
	METRIC_MIN: 91,
	METRIC_MAX: 241,

	// in ft
	IMPERIAL_MIN: 3,
	IMPERIAL_MAX: 7,
};

export const HEIGHT_RANGE = {
	METRIC: makeMetricHeightRange(HEIGHT_BOUNDS.METRIC_MIN, HEIGHT_BOUNDS.METRIC_MAX),
	IMPERIAL: makeImperialHeightRange(HEIGHT_BOUNDS.IMPERIAL_MIN, HEIGHT_BOUNDS.IMPERIAL_MAX),
};

export const UNIT_COPY = {
	[FIELDS_MAP.HEIGHT]: {
		IMPERIAL: _str("Change to Imperial"),
		METRIC: _str("Change to Metric"),
	},
};

export const PROMPT_COMMENT_TIMEOUT = 2500;

export const PROMPT_COMMENT = {
	skip: [
		_str("FYI this limits who sees you."),
	],
};

export const ERROR = {
	[FIELDS_MAP.BIRTHDATE]: {
		UNDERAGE: _str("You must be at least 18 years old"),
		MISSING: _str("Something's missing!"),
		INVALID: _str("Invalid age."),
	},

	[FIELDS_MAP.LOCATION]: {
		ZIP_LENGTH: _str("Zipcode must be 5 characters."),
		ZIP_NUMBER: _str("Zipcode must be all numbers."),
		REQUIRED: _str("Location is required."),
	},
};

export const UNITED_STATES = "United States";
export const INDIA = "India";
export const BINARY_GENDERS = { man: "man", woman: "woman" };

export const PRONOUN_LOOKUP = {
	"He/Him": "male",
	"She/Her": "female",
	"They/Them": "nonbinary",
};

export const REVERSE_PRONOUN_LOOKUP = {
	male: "He/Him",
	female: "She/Her",
	nonbinary: "They/Them",
};

export const EMOJI_REGEX = /[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|[\ud83c[\ude32-\ude3a]|[\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff]/;

export const MONTHS = [
	{ value: 1, name: "January" },
	{ value: 2, name: "February" },
	{ value: 3, name: "March" },
	{ value: 4, name: "April" },
	{ value: 5, name: "May" },
	{ value: 6, name: "June" },
	{ value: 7, name: "July" },
	{ value: 8, name: "August" },
	{ value: 9, name: "September" },
	{ value: 10, name: "October" },
	{ value: 11, name: "November" },
	{ value: 12, name: "December" },
];

export const EDUCATION_LEVEL_OPTIONS = [
	{ name: _str("High school"), value: "1" },
	{ name: _str("Trade/tech school"), value: "2" },
	{ name: _str("In college"), value: "10" },
	{ name: _str("Undergraduate degree"), value: "11" },
	{ name: _str("In grad school"), value: "12" },
	{ name: _str("Graduate degree"), value: "13" },
];

export const PETS_OPTIONS = [
	{ name: _str("Has dog(s)"), value: 4 },
	{ name: _str("Has cat(s)"), value: 3 },
	{ name: _str("Has other pet(s)"), value: 2 },
	{ name: _str("Doesn't have pet(s)"), value: 1 },
];

export const IN_SCHOOL_VALUES = new Set(["10", "12"]);

export const COMPLETED_SCHOOL_VALUES = new Set(["2", "11", "13"]);

export const ORIENTATION_TAGS_OPTIONS = AVAILABLE_TAGS.orientation
	.map(({ name, id: value }) => ({ name, value }));

export const FULL_OR_PART_TIME = new Set(["full_time", "part_time"]);
export const FREELANCE_OR_SELF = new Set(["freelance", "self_employed"]);

export const WANTS_MORE_CHILDREN_OPTIONS = [
	{ name: _str("Wants more kid(s)"), value: "wants" },
	{ name: _str("Might want more kid(s)"), value: "might_want" },
	{ name: _str("Doesn’t want more kid(s)"), value: "doesnt_want" },
];

export const WANTS_CHILDREN_OPTIONS = [
	{ name: _str("Wants kid(s)"), value: "wants" },
	{ name: _str("Might want kid(s)"), value: "might_want" },
	{ name: _str("Doesn’t want kid(s)"), value: "doesnt_want" },
];

export const HAS_CHILDREN_VALUE = "has";

export const STRAIGHT_TOGGLE_COPY = "Only show me to straight $genders";
export const UNDERAGE_STATUS = "TOO YOUNG";
export const FACEBOOK_UNDERAGE_STATUS = "CAF_UNDERAGE";

// detail names by their backend id from libcupid_defs.h
// https://okgithub.office.okcupid.com/OkCupid/okcupid/blob/master/cupid/libcupid/libcupid_defs.h#L31
export const COMPLETED_DETAILS_TO_FIELD = {
	"0": null, // unknown
	"1": FIELDS_MAP.ORIENTATION_TAGS,
	"2": FIELDS_MAP.AGE,
	"3": FIELDS_MAP.HEIGHT,
	"4": FIELDS_MAP.BODY_TYPE,
	"5": FIELDS_MAP.LOCATION,
	"6": FIELDS_MAP.GENDER_TAGS,
	"7": FIELDS_MAP.MONOGAMY,
	"8": FIELDS_MAP.LOO,
	"10": FIELDS_MAP.RELIGION_VALUE,
	"11": FIELDS_MAP.ETHNICITIES,
	"12": FIELDS_MAP.POLITICS,
	"13": FIELDS_MAP.EDUCATION_LEVEL,
	"14": FIELDS_MAP.EMPLOYMENT,
	"15": FIELDS_MAP.CHILDREN_HAVE,
	"16": FIELDS_MAP.SIGNS,
	"17": FIELDS_MAP.LANGUAGES,
	"18": FIELDS_MAP.PETS,
	"19": FIELDS_MAP.SMOKING,
	"20": FIELDS_MAP.DRINKING,
	"21": FIELDS_MAP.MARIJUANA,
	"22": FIELDS_MAP.DIET,
};



// WEBPACK FOOTER //
// ./src/apps/profile_details/util/constants.js